/**
 * Turns technical failures into one user-visible notification each.
 *
 * Sources: failing graph nodes (via the runtime error reporter), uncaught errors
 * and unhandled promise rejections. Technical details are only shown in draft
 * preview or dev builds; end users just get a plain-language line.
 *
 * `main.ts` imports this module first so the global handlers are installed
 * before any other module (e.g. `scene-builder`s top-level await) can fail.
 */

import { setGraphErrorReporter, toErrorMessage, type GraphErrorReport } from '@spotar/runtime';
import { showNotification } from './notification';
import { getMode } from './session-token.js';

/** Node types whose failure has a meaning the user can actually understand. */
const NODE_FAILURE_TITLES: Record<string, string> = {
  textToSpeech: 'Sprachausgabe konnte nicht erzeugt werden',
  translateText: 'Übersetzung ist fehlgeschlagen',
  playAudio: 'Audio konnte nicht abgespielt werden',
  stopAudio: 'Audio konnte nicht gestoppt werden',
  playerShow: 'Audio-Player konnte nicht angezeigt werden',
  playerLoadTrack: 'Audio konnte nicht geladen werden',
  playerPlay: 'Audio konnte nicht abgespielt werden',
  playerPause: 'Audio konnte nicht pausiert werden',
  playAnimation: 'Animation konnte nicht gestartet werden',
  playVideo: 'Video konnte nicht abgespielt werden',
  prefetchAsset: 'Inhalt konnte nicht vorgeladen werden',
  traySpawnChoice: 'Auswahl konnte nicht geladen werden',
  trayHighlight: 'Hinweis auf das Tray-Element konnte nicht gesetzt werden',
  trayDisableInput: 'Tray-Eingabe konnte nicht deaktiviert werden',
  trayEnableInput: 'Tray-Eingabe konnte nicht aktiviert werden',
  callFunction: 'Funktion konnte nicht ausgeführt werden',
  callCustomEvent: 'Custom Event konnte nicht ausgelöst werden',
};

const KIND_FALLBACK_TITLES: Record<GraphErrorReport['kind'], string> = {
  action: 'Eine Aktion konnte nicht ausgeführt werden',
  event: 'Eine Interaktion konnte nicht aktiviert werden',
  exec: 'Eine Aktion konnte nicht ausgeführt werden',
  configuration: 'Diese Erfahrung ist nicht vollständig eingerichtet',
};

let isInstalled = false;

function installErrorNotifications(): void {
  if (isInstalled) return;
  isInstalled = true;
  setGraphErrorReporter(notifyGraphError);
  window.addEventListener('error', onUncaughtError);
  window.addEventListener('unhandledrejection', onUnhandledRejection);
}

/** For failures the viewer catches itself (scene loading, XR startup, …). */
export function notifyViewerFailure(failure: { title: string; error?: unknown }): void {
  showNotification({
    title: failure.title,
    detail: buildTechnicalDetail(failure.error),
    tone: 'error',
    dedupeKey: `viewer|${failure.title}|${toErrorMessage(failure.error)}`,
  });
}

function notifyGraphError(report: GraphErrorReport): void {
  showNotification({
    title: NODE_FAILURE_TITLES[report.nodeType] ?? KIND_FALLBACK_TITLES[report.kind],
    detail: buildGraphErrorDetail(report),
    tone: report.kind === 'configuration' ? 'warning' : 'error',
    dedupeKey: `graph|${report.nodeId}|${report.summary}`,
  });
}

function onUncaughtError(event: ErrorEvent | Event): void {
  // Failed <img>/<script> loads also fire "error" on window, but as plain Events.
  if (!(event instanceof ErrorEvent)) return;
  notifyUnexpectedFailure(event.error ?? event.message);
}

function onUnhandledRejection(event: PromiseRejectionEvent): void {
  notifyUnexpectedFailure(event.reason);
}

function notifyUnexpectedFailure(error: unknown): void {
  showNotification({
    title: 'Es ist ein unerwarteter Fehler aufgetreten',
    detail: buildTechnicalDetail(error),
    tone: 'error',
    dedupeKey: `unexpected|${toErrorMessage(error)}`,
  });
}

function buildGraphErrorDetail(report: GraphErrorReport): string | undefined {
  if (!isTechnicalDetailVisible()) return undefined;
  const cause = report.error === undefined ? '' : ` – ${toErrorMessage(report.error)}`;
  return `${report.nodeType} (${report.nodeId}): ${report.summary}${cause}`;
}

function buildTechnicalDetail(error: unknown): string | undefined {
  if (!isTechnicalDetailVisible()) return undefined;
  return toErrorMessage(error);
}

/** Raw error text helps while authoring, but only confuses visitors of a live spot. */
function isTechnicalDetailVisible(): boolean {
  return import.meta.env.DEV || getMode() === 'draft';
}

installErrorNotifications();
